import { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { GET_EVENT_BOOKING_MANIFEST } from "@app/graphql/events.queries";

export interface BookingManifestEntry {
  bookingManifestId: string;
  pnr: string | null;
  manifestPassengerId: string | null;
  ticketNumber: string | null;
  passengerName: string | null;
  flightNumber: string | null;
  bookingStatus: string | null;
  currency: string | null;
  amountPaid: number | null;
  snapshotAt: string | null;
  updatedAt: string | null;
}

type BookingManifestPageResponse = {
  getEventBookingManifest: {
    count: number;
    cursor: string | null;
    snapshotAt: string | null;
    items: BookingManifestEntry[];
  };
};

type UseEventBookingManifestPageOptions = {
  /** Event scope for the query. The hook idles until this is set. */
  eventId: string | null | undefined;
  /** Number of rows shown per page. */
  pageSize: number;
  /** When false, the hook clears results and skips fetching. */
  enabled?: boolean;
};

type UseEventBookingManifestPageResult = {
  /** Rows for the current page. */
  items: BookingManifestEntry[];
  snapshotAt: string | null;
  /** Total number of manifest rows across all pages. */
  count: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  setPage: (page: number) => void;
};

/**
 * Paginates `getEventBookingManifest` server-side via cursors and exposes
 * `page`/`totalPages`/`setPage` for the shared `PaginationPageDefault` control.
 */
export function useEventBookingManifestPage(
  options: UseEventBookingManifestPageOptions,
): UseEventBookingManifestPageResult {
  const { eventId, pageSize, enabled = true } = options;
  const apolloClient = useApolloClient();

  const [page, setPage] = useState(1);
  const [items, setItems] = useState<BookingManifestEntry[]>([]);
  const [count, setCount] = useState(0);
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null);
  const [maxKnownPage, setMaxKnownPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Server mode: cursor used to fetch each 1-based page (page 1 has none).
  const cursorsRef = useRef<Map<number, string | undefined>>(
    new Map([[1, undefined]]),
  );

  // A change to the event or page size invalidates discovered cursors.
  const queryKey = `${eventId ?? ""}|${pageSize}`;
  const prevQueryKeyRef = useRef(queryKey);

  useEffect(() => {
    if (!enabled || !eventId) {
      setItems([]);
      setCount(0);
      setSnapshotAt(null);
      setMaxKnownPage(1);
      cursorsRef.current = new Map([[1, undefined]]);
      setIsLoading(false);
      return;
    }

    let pageToFetch = page;

    if (prevQueryKeyRef.current !== queryKey) {
      prevQueryKeyRef.current = queryKey;
      cursorsRef.current = new Map([[1, undefined]]);
      setMaxKnownPage(1);
      if (page !== 1) {
        setPage(1); // re-runs this effect with page === 1
        return;
      }
      pageToFetch = 1;
    }

    let isCancelled = false;
    setIsLoading(true);

    const run = async () => {
      try {
        const cursor = cursorsRef.current.get(pageToFetch);
        const { data } = await apolloClient.query<BookingManifestPageResponse>({
          query: GET_EVENT_BOOKING_MANIFEST,
          variables: {
            eventId,
            limit: pageSize,
            ...(cursor ? { cursor } : {}),
          },
          fetchPolicy: "network-only",
        });
        if (isCancelled) return;

        const manifest = data?.getEventBookingManifest;
        const rows = manifest?.items ?? [];
        const nextCursor = manifest?.cursor ?? null;
        setSnapshotAt(manifest?.snapshotAt ?? null);

        if (nextCursor) {
          // Server mode: advance the discovered range.
          setItems(rows);
          setCount(manifest?.count ?? rows.length);
          cursorsRef.current.set(pageToFetch + 1, nextCursor);
          setMaxKnownPage((current) => Math.max(current, pageToFetch + 1));
        } else {
          // Server mode last/only page: clamp the discovered range.
          setItems(rows);
          setCount(manifest?.count ?? rows.length);
          setMaxKnownPage(pageToFetch);
          for (const knownPage of [...cursorsRef.current.keys()]) {
            if (knownPage > pageToFetch) {
              cursorsRef.current.delete(knownPage);
            }
          }
        }
      } catch {
        if (!isCancelled) {
          setItems([]);
          setCount(0);
          setSnapshotAt(null);
          setMaxKnownPage(1);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void run();

    return () => {
      isCancelled = true;
    };
  }, [apolloClient, eventId, queryKey, page, pageSize, enabled]);

  return {
    items,
    snapshotAt,
    count,
    page: Math.min(page, maxKnownPage),
    totalPages: maxKnownPage,
    isLoading,
    setPage,
  };
}
